import { DateTime } from "luxon";
import {
  HelpAssistTicketInfoDto,
  HelpAssistTicketInfoLastAppointmentActivityDto,
} from "mock-api/src/shared-v2-types";
import { Site } from "src/app/core/sites";
import { ActivityAction } from "src/app/partner/settings/activities/base-activity.model";
import {
  createGlobalResourceUrl,
  createPartnerResourceUrl,
  getApiDetailsDecorator,
  getEnumMember,
  parseDateTime,
} from "src/utils";
import { Attachment } from "../../attachment.model";
import {
  HelpAssistTicketStatus,
  HelpAssistTicketType,
} from "../../help-assist-enums";
import { HelpAssistEvent } from "../../help-assist-event.model";
import { HelpAssistUser } from "../../help-assist-user.model";
import { HelpAssistTicketDock } from "../help-assist-ticket-dock.model";
import { HelpAssistTicketResolution } from "../help-assist-ticket-resolution.model";
import { PartnerHelpAssistTicketSchedule } from "../ticket-schedule/help-assist-ticket-appointment-schedule.model";
import {
  BaseHelpAssistTicket,
  getHelpAssistTicketRouteUrl,
  HelpAssistTicketReference,
} from "./base-help-assist-ticket.model";

interface PartnerDeserializeArguments {
  readonly site: Site;
}

export const helpTicketsResourcePartner =
  createPartnerResourceUrl("HelpAssistTickets");

export const helpTicketsResourceGlobal =
  createGlobalResourceUrl("HelpAssistTickets");

const api = getApiDetailsDecorator<HelpAssistTicketInfoDto>();

type ComputedProperty = "isResolved";

export class PartnerHelpAssistTicket
  extends BaseHelpAssistTicket
  implements HelpAssistTicketReference
{
  public constructor(
    args: Omit<ClassProperties<PartnerHelpAssistTicket>, ComputedProperty>,
  ) {
    super(args);
    this.assignedUser = args.assignedUser;
    this.attachments = args.attachments;
    this.category = args.category;
    this.createdOn = args.createdOn;
    this.dock = args.dock;
    this.events = args.events;
    this.id = args.id;
    this.isAutoApproved = args.isAutoApproved;
    this.isRead = args.isRead;
    this.lastEventCreatedOn = args.lastEventCreatedOn;
    this.orders = args.orders;
    this.ownerUser = args.ownerUser;
    this.resolutions = args.resolutions;
    this.schedule = args.schedule;
    this.status = args.status;
    this.updatedOn = args.updatedOn;

    // Computed Properties

    this.isResolved =
      this.status === HelpAssistTicketStatus.Closed ||
      this.status === HelpAssistTicketStatus.Expired;
  }

  @api({ key: "assignedUser", uiModel: HelpAssistUser })
  public readonly assignedUser: HelpAssistUser | null;
  public readonly attachments: readonly Attachment[];
  @api() public readonly category: string | null;
  @api() public readonly createdOn: DateTime;
  @api({
    key: "dock",
    uiModel: HelpAssistTicketDock,
  })
  public readonly dock: HelpAssistTicketDock | null;
  @api() public readonly events: readonly HelpAssistEvent[];
  @api() public readonly id: HelpAssistTicketReference["id"];
  @api() public readonly isAutoApproved: boolean;
  @api() public readonly isRead: boolean;
  @api() public readonly lastEventCreatedOn: DateTime;
  @api() public readonly orders: string | null;
  @api({
    key: "ownerUser",
    uiModel: HelpAssistUser,
  })
  public readonly ownerUser: HelpAssistUser;
  public readonly resolutions: readonly HelpAssistTicketResolution[];
  @api({
    key: "appointmentSchedule",
    uiModel: PartnerHelpAssistTicketSchedule,
  })
  public readonly schedule: PartnerHelpAssistTicketSchedule | null;
  @api() public readonly status: HelpAssistTicketStatus;
  @api() public readonly updatedOn: DateTime;

  public readonly isResolved: boolean;

  public static deserialize(
    apiModel: HelpAssistTicketInfoDto,
    { site }: PartnerDeserializeArguments,
  ): PartnerHelpAssistTicket {
    const ownerUser = HelpAssistUser.deserialize(apiModel.ownerUser);

    return new PartnerHelpAssistTicket({
      assignedUser: apiModel.assignedUser
        ? HelpAssistUser.deserialize(apiModel.assignedUser)
        : null,
      attachments: Attachment.deserializeList(apiModel.attachments, { site }),
      category: apiModel.category ?? null,
      createdOn: parseDateTime(apiModel.createdOn, site),
      dock: apiModel.dock
        ? HelpAssistTicketDock.deserialize(apiModel.dock)
        : null,
      emailAddress: apiModel.emailAddress,
      events: HelpAssistEvent.deserializeList(apiModel.events, { ownerUser }),
      id: apiModel.id,
      isAutoApproved: apiModel.isAutoApproved,
      isRead: apiModel.isRead,
      lastEventCreatedOn: parseDateTime(apiModel.lastEventCreatedOn, site),
      orders: apiModel.orders ?? null,
      ownerUser,
      phoneNumber: apiModel.phoneNumber ?? null,
      resolutions: HelpAssistTicketResolution.deserializeList(
        apiModel.resolutions,
      ),
      schedule: apiModel.appointmentSchedule
        ? PartnerHelpAssistTicketSchedule.deserialize(
            apiModel.appointmentSchedule,
            { site },
          )
        : null,
      site,
      status: getEnumMember(HelpAssistTicketStatus, apiModel.status),
      type: getEnumMember(HelpAssistTicketType, apiModel.type),
      updatedOn: parseDateTime(apiModel.updatedOn, site),
    });
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getHelpAssistTicketRouteUrl(this, ...childPaths);
  }
}

export type HelpAssistTicket = PartnerHelpAssistTicket;

interface TicketActivityDeserializeArguments {
  site: Site;
}

export function deserializeTicketActivity(
  data: HelpAssistTicketInfoLastAppointmentActivityDto,
  { site }: TicketActivityDeserializeArguments,
): TicketActivity {
  return new TicketActivity({
    action: getEnumMember(ActivityAction, data.action),
    activityDate: parseDateTime(data.activityDate, site),
    username: data.username || "unknown",
  });
}

export class TicketActivity {
  public constructor(args: ClassProperties<TicketActivity>) {
    this.action = args.action;
    this.activityDate = args.activityDate;
    this.username = args.username;
  }

  public readonly action: ActivityAction;
  public readonly activityDate: DateTime;
  public readonly username: string;
}
